/**
 * Quick View Modal - Cosmic Polish / Dawn Theme
 * Fetches product data via Shopify's /products/[handle].js endpoint
 * and renders a full quick-view modal with image gallery + product info.
 */

(function () {
  'use strict';

  // --- State ---
  let currentImages = [];
  let currentImageIndex = 0;
  let currentProduct = null;
  let selectedVariantId = null;

  // --- DOM refs ---
  const modal = document.getElementById('quick-view-modal');
  const loading = document.getElementById('qv-loading');
  const productEl = document.getElementById('qv-product');
  const titleEl = document.getElementById('qv-title');
  const vendorTypeEl = document.getElementById('qv-vendor-type');
  const priceEl = document.getElementById('qv-price');
  const variantsEl = document.getElementById('qv-variants');
  const descEl = document.getElementById('qv-description');
  const fullLinkEl = document.getElementById('qv-full-link');
  const thumbnailsEl = document.getElementById('qv-thumbnails');
  const mainImageEl = document.getElementById('qv-main-image');
  const prevBtn = document.getElementById('qv-prev');
  const nextBtn = document.getElementById('qv-next');
  const addToCartBtn = document.getElementById('qv-add-to-cart');
  const qtyInput = document.getElementById('qv-qty');

  if (!modal) return;

  // --- Open / Close ---
  function openModal() {
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('qv-modal-open');
    // Trap focus
    setTimeout(() => {
      const firstFocusable = modal.querySelector('button, [href], input, select, textarea');
      if (firstFocusable) firstFocusable.focus();
    }, 100);
  }

  function closeModal() {
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('qv-modal-open');
    // Reset state
    currentImages = [];
    currentImageIndex = 0;
    currentProduct = null;
    selectedVariantId = null;
    loading.style.display = 'flex';
    productEl.style.display = 'none';
    qtyInput.value = 1;
    // Reset ATC button
    const atcText = addToCartBtn.querySelector('.qv-add-to-cart__text');
    const atcLoading = addToCartBtn.querySelector('.qv-add-to-cart__loading');
    atcText.textContent = 'Add to cart';
    atcText.style.display = '';
    atcLoading.style.display = 'none';
    addToCartBtn.classList.remove('qv-add-to-cart--success', 'qv-add-to-cart--sold-out');
    addToCartBtn.disabled = false;
  }

  // Close handlers
  modal.querySelectorAll('[data-qv-close]').forEach((el) => {
    el.addEventListener('click', closeModal);
  });

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      closeModal();
    }
  });

  // --- Fetch product + render ---
  async function loadProduct(handle) {
    openModal();
    loading.style.display = 'flex';
    productEl.style.display = 'none';

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Product not found');
      const product = await res.json();
      currentProduct = product;

      renderProduct(product);

      loading.style.display = 'none';
      productEl.style.display = '';
    } catch (err) {
      console.error('Quick View: Failed to load product', err);
      loading.innerHTML = '<p style="color:#c0392b;text-align:center;padding:20px;">Sorry, could not load this product. Please try the product page.</p>';
    }
  }

  function renderProduct(product) {
    // Title
    titleEl.innerHTML = `<a href="/products/${product.handle}" class="qv-product__title-link">${product.title}</a>`;

    // Vendor + Type
    let vendorHtml = '';
    if (product.vendor) {
      vendorHtml += `Vendor: <a href="/collections/vendors?q=${encodeURIComponent(product.vendor)}">${product.vendor}</a>`;
    }
    if (product.type) {
      if (vendorHtml) vendorHtml += '  |  ';
      vendorHtml += `Type: <a href="/collections/types?q=${encodeURIComponent(product.type)}">${product.type}</a>`;
    }
    vendorTypeEl.innerHTML = vendorHtml;

    // Images
    currentImages = product.images || [];
    currentImageIndex = 0;
    renderThumbnails();
    renderMainImage();

    // Select first available variant
    const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
    selectedVariantId = firstAvailable.id;

    // Price
    renderPrice(firstAvailable);

    // Variants
    renderVariants(product);

    // Description (HTML from Shopify)
    descEl.innerHTML = product.description || '';

    // Full details link
    fullLinkEl.href = `/products/${product.handle}`;

    // ATC state
    updateATCState(firstAvailable);
  }

  // --- Images ---
  function renderThumbnails() {
    thumbnailsEl.innerHTML = '';
    currentImages.forEach((src, i) => {
      const thumb = document.createElement('button');
      thumb.className = 'qv-thumb' + (i === currentImageIndex ? ' qv-thumb--active' : '');
      thumb.setAttribute('aria-label', `View image ${i + 1}`);
      thumb.innerHTML = `<img src="${getSizedUrl(src, '100x100')}" alt="Product thumbnail ${i + 1}" loading="lazy">`;
      thumb.addEventListener('click', () => goToImage(i));
      thumbnailsEl.appendChild(thumb);
    });
  }

  function renderMainImage() {
    if (!currentImages.length) {
      mainImageEl.innerHTML = '<p style="color:#999;padding:20px;">No image available</p>';
      return;
    }
    const src = currentImages[currentImageIndex];
    mainImageEl.innerHTML = `<img src="${getSizedUrl(src, '800x')}" alt="${currentProduct ? currentProduct.title : 'Product image'}">`;

    // Update active thumbnail
    const thumbs = thumbnailsEl.querySelectorAll('.qv-thumb');
    thumbs.forEach((t, i) => {
      t.classList.toggle('qv-thumb--active', i === currentImageIndex);
    });

    // Scroll active thumbnail into view
    if (thumbs[currentImageIndex]) {
      thumbs[currentImageIndex].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }

    // Arrow visibility
    prevBtn.style.display = currentImages.length > 1 ? '' : 'none';
    nextBtn.style.display = currentImages.length > 1 ? '' : 'none';
  }

  function goToImage(index) {
    currentImageIndex = index;
    renderMainImage();
  }

  // Arrow nav
  prevBtn.addEventListener('click', () => {
    if (currentImages.length < 2) return;
    currentImageIndex = (currentImageIndex - 1 + currentImages.length) % currentImages.length;
    renderMainImage();
  });

  nextBtn.addEventListener('click', () => {
    if (currentImages.length < 2) return;
    currentImageIndex = (currentImageIndex + 1) % currentImages.length;
    renderMainImage();
  });

  // --- Price ---
  function renderPrice(variant) {
    const price = formatMoney(variant.price);
    let html = price;
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      html += ` <span class="qv-compare-price">${formatMoney(variant.compare_at_price)}</span>`;
    }
    priceEl.innerHTML = html;
  }

  // --- Variants ---
  function renderVariants(product) {
    // If only one variant with title "Default Title", hide variant picker
    if (product.variants.length === 1 && product.variants[0].title === 'Default Title') {
      variantsEl.style.display = 'none';
      return;
    }

    variantsEl.style.display = '';
    variantsEl.innerHTML = '';

    product.options.forEach((option, optionIndex) => {
      const group = document.createElement('div');
      group.className = 'qv-variant-group';

      const label = document.createElement('div');
      label.className = 'qv-variant-group__label';
      label.textContent = option;
      group.appendChild(label);

      const optionsWrap = document.createElement('div');
      optionsWrap.className = 'qv-variant-options';

      // Get unique values for this option
      const values = [...new Set(product.variants.map((v) => v.options[optionIndex]))];

      values.forEach((val) => {
        const btn = document.createElement('button');
        btn.className = 'qv-variant-option';
        btn.textContent = val;
        btn.type = 'button';

        // Check if this value matches the currently selected variant
        const currentVariant = product.variants.find((v) => v.id === selectedVariantId);
        if (currentVariant && currentVariant.options[optionIndex] === val) {
          btn.classList.add('qv-variant-option--active');
        }

        btn.addEventListener('click', () => {
          // Find the new variant by matching options
          const currentVar = product.variants.find((v) => v.id === selectedVariantId);
          const newOptions = [...(currentVar ? currentVar.options : [])];
          newOptions[optionIndex] = val;

          // Find matching variant
          const newVariant = product.variants.find((v) =>
            v.options.every((o, i) => o === newOptions[i])
          );

          if (newVariant) {
            selectedVariantId = newVariant.id;
            renderPrice(newVariant);
            updateATCState(newVariant);

            // Update variant image if it has one
            if (newVariant.featured_image && newVariant.featured_image.src) {
              const imgIndex = currentImages.findIndex(
                (img) => img === newVariant.featured_image.src
              );
              if (imgIndex !== -1) {
                goToImage(imgIndex);
              }
            }
          }

          // Re-render variant selectors to update active states
          renderVariants(product);
        });

        optionsWrap.appendChild(btn);
      });

      group.appendChild(optionsWrap);
      variantsEl.appendChild(group);
    });
  }

  // --- Add to cart ---
  function updateATCState(variant) {
    const atcText = addToCartBtn.querySelector('.qv-add-to-cart__text');
    addToCartBtn.classList.remove('qv-add-to-cart--sold-out', 'qv-add-to-cart--success');

    if (!variant.available) {
      atcText.textContent = 'Sold out';
      addToCartBtn.disabled = true;
      addToCartBtn.classList.add('qv-add-to-cart--sold-out');
    } else {
      atcText.textContent = 'Add to cart';
      addToCartBtn.disabled = false;
    }
  }

  addToCartBtn.addEventListener('click', async () => {
    if (!selectedVariantId || addToCartBtn.disabled) return;

    const atcText = addToCartBtn.querySelector('.qv-add-to-cart__text');
    const atcLoading = addToCartBtn.querySelector('.qv-add-to-cart__loading');
    const qty = parseInt(qtyInput.value, 10) || 1;

    // Loading state
    atcText.style.display = 'none';
    atcLoading.style.display = '';
    addToCartBtn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: selectedVariantId, quantity: qty }],
        }),
      });

      if (!res.ok) throw new Error('Add to cart failed');

      // Success
      atcLoading.style.display = 'none';
      atcText.style.display = '';
      atcText.textContent = 'Added! ✓';
      addToCartBtn.classList.add('qv-add-to-cart--success');

      // Update cart count in header (Dawn uses a cart-icon-bubble or similar)
      updateCartCount();

      setTimeout(() => {
        atcText.textContent = 'Add to cart';
        addToCartBtn.classList.remove('qv-add-to-cart--success');
        addToCartBtn.disabled = false;
      }, 2000);
    } catch (err) {
      console.error('Quick View: Add to cart error', err);
      atcLoading.style.display = 'none';
      atcText.style.display = '';
      atcText.textContent = 'Error – try again';
      addToCartBtn.disabled = false;

      setTimeout(() => {
        atcText.textContent = 'Add to cart';
      }, 2500);
    }
  });

  // --- Quantity buttons ---
  document.querySelectorAll('[data-qv-qty]').forEach((btn) => {
    btn.addEventListener('click', () => {
      let val = parseInt(qtyInput.value, 10) || 1;
      if (btn.dataset.qvQty === 'minus') {
        val = Math.max(1, val - 1);
      } else {
        val += 1;
      }
      qtyInput.value = val;
    });
  });

  // --- Update cart count (Dawn compatible) ---
async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const cartLink = document.getElementById('cart-icon-bubble');
      if (!cartLink) return;

      let bubble = cartLink.querySelector('.cart-count-bubble');

      if (cart.item_count > 0) {
        if (!bubble) {
          // Create the bubble if it doesn't exist yet
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
          cartLink.appendChild(bubble);
        }
        bubble.querySelector('span[aria-hidden="true"]').textContent = cart.item_count;
        bubble.querySelector('.visually-hidden').textContent = cart.item_count + ' item' + (cart.item_count > 1 ? 's' : '');
      }
    } catch (e) {
      // Silently fail
    }
  }

  // --- Helpers ---
  function getSizedUrl(url, size) {
    // Shopify image URL sizing: insert _SIZExSIZE before extension
    if (!url) return '';
    // Handle already-sized URLs
    const match = url.match(/^(.*?)(\.\w+)(\?.*)?$/);
    if (!match) return url;
    // Check if already has a size suffix
    const base = match[1].replace(/_\d+x\d*/, '');
    return `${base}_${size}${match[2]}${match[3] || ''}`;
  }

  function formatMoney(cents) {
    // Uses the shop's money format — for simplicity, format as $X.XX
    // You can replace this with Shopify.formatMoney if theme includes it
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  // --- Event delegation for trigger buttons ---
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-qv-handle]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const handle = trigger.dataset.qvHandle;
      if (handle) loadProduct(handle);
      return false;
    }
  }, true);

  // --- Swipe support for mobile ---
  let touchStartX = 0;
  let touchEndX = 0;

  mainImageEl.addEventListener('touchstart', (e) => {
    touchStartX = e.changedTouches[0].screenX;
  }, { passive: true });

  mainImageEl.addEventListener('touchend', (e) => {
    touchEndX = e.changedTouches[0].screenX;
    const diff = touchStartX - touchEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // Swipe left -> next
        nextBtn.click();
      } else {
        // Swipe right -> prev
        prevBtn.click();
      }
    }
  }, { passive: true });

  // Position quick-view buttons at bottom of image area
  function positionQVButtons() {
    document.querySelectorAll('.card-wrapper .qv-trigger').forEach(btn => {
      const wrapper = btn.closest('.card-wrapper');
      const inner = wrapper?.querySelector('.card__inner');
      if (inner) {
        btn.style.top = (inner.offsetHeight - 45) + 'px';
      }
    });
  }

  positionQVButtons();
  window.addEventListener('resize', positionQVButtons);
  window.addEventListener('load', positionQVButtons);
})();
